import {
  Box,
  type Dimensions,
  Icon,
  Table,
  Text,
  useClassNames,
} from '@factorialco/deprecated-design-system'
import { isEmpty, some, values } from 'lodash'

import WorksheetCell from 'modules/payroll_bundle/components/common/components/WorksheetCell'
import type {
  ResultMatches,
  SupplementsMatches,
  WorksheetData,
} from 'modules/payroll_bundle/components/common/helper/worksheet'
import * as styles from 'modules/payroll_bundle/components/common/styles/index.vanilla.css'
import { Actions as ResultContextActions } from 'modules/payroll_bundle/lib/PayrollResultContext'
import { Actions as BulkImportSupplementsContextActions } from 'modules/payroll_bundle/lib/bulkImportSupplementsContext'
import {
  MATCH_COL_INDEX,
  MATCH_MAX_COLUMNS,
  MATCH_MAX_ROWS,
  MATCH_ROW_INDEX,
  UPDATE_DATA,
} from 'modules/payroll_bundle/lib/constants'

interface SequentialButtons {
  id: string
  order: number
  required: boolean
}

interface Props {
  worksheetData: WorksheetData
  worksheetMatches: ResultMatches | SupplementsMatches
  header?: React.ReactNode
  activeButton: string
  dispatch?:
    | React.Dispatch<BulkImportSupplementsContextActions>
    | React.Dispatch<ResultContextActions>
  padding?: Dimensions
  workSheetInformation?: Record<string, string>
  allowZeroValues?: boolean
  sequentialButtons?: SequentialButtons[]
  onWorksheetMatchesChange?: (worksheetMatches: ResultMatches | SupplementsMatches) => void
  onClickButton?: (button: string) => void
}

const WorksheetTable = ({
  header,
  activeButton,
  worksheetData,
  dispatch,
  padding = 's40',
  worksheetMatches,
  workSheetInformation,
  allowZeroValues = false,
  sequentialButtons = [],
  onWorksheetMatchesChange,
  onClickButton,
}: Props) => {
  const cx = useClassNames(styles)

  const colName = (n: number) => {
    const ordA = 'A'.charCodeAt(0)
    const ordZ = 'Z'.charCodeAt(0)
    const len = ordZ - ordA + 1
    let s = ''

    while (n >= 0) {
      s = String.fromCharCode((n % len) + ordA) + s
      n = Math.floor(n / len) - 1
    }

    return s
  }

  const nextActiveButton = () => {
    const missingSelection = sequentialButtons
      .sort((a, b) => a.order - b.order)
      .filter((button) => button.id !== activeButton)

    const nextButton = missingSelection.find(
      (button) => !worksheetMatches[button.id] && button.required
    )

    return nextButton?.id || ''
  }

  const setCellMatch = (rowIndex: number, colIndex: number, value: string | number) => () => {
    if (!!activeButton && isClickable(rowIndex, colIndex)) {
      const cellMatch: Record<string, [number, number, string | number]> = {}

      cellMatch[activeButton] = [rowIndex, colIndex, value]
      // => { last_name: [1, 1, 'Doe'] }

      if (dispatch) {
        dispatch({
          type: UPDATE_DATA,
          payload: {
            activeButton: nextActiveButton(),
            worksheetMatches: { ...worksheetMatches, ...cellMatch },
          },
        })
      }
      onWorksheetMatchesChange?.({ ...worksheetMatches, ...cellMatch })
      onClickButton?.(nextActiveButton())
    }
  }

  const isClickable = (rowIndex: number, colIndex: number): boolean => {
    // Only allow same row or column selection
    if (isEmpty(worksheetMatches)) return true

    const matches = values(worksheetMatches)
    const inSameRow: boolean = rowIndex === matches[0]![MATCH_ROW_INDEX]
    const inSameColumn: boolean = colIndex === matches[0]![MATCH_COL_INDEX]

    // define this here as functions because of no-case-declarations lint error inside case
    const matchByColumn = (): boolean =>
      matches[0]![MATCH_COL_INDEX] === matches[1]![MATCH_COL_INDEX]

    const matchByRow = (): boolean => matches[0]![MATCH_ROW_INDEX] === matches[1]![MATCH_ROW_INDEX]

    switch (matches.length) {
      case 1:
        return inSameRow || inSameColumn

      default:
        if (matchByColumn() && matchByRow()) {
          // i.e. name and last_name can point to the same cell
          return inSameRow || inSameColumn
        } else if (matchByColumn()) {
          return inSameColumn
        } else if (matchByRow()) {
          return inSameRow
        }

        return false
    }
  }

  return (
    <Box width="full" flexDirection="column" alignItems="center" padding={padding}>
      {workSheetInformation && (
        <Box gap="s16" alignItems="center" className={cx('centerBox')}>
          <Icon.Large icon="common-file-text-upload" />
          <Text.Header2>{workSheetInformation.title}</Text.Header2>
          <Box textAlign="center">
            <Text.Normal>{workSheetInformation.description}</Text.Normal>
          </Box>
        </Box>
      )}
      <Box width="full">
        {header}
        <Box overflowX="auto" width="full">
          <Box
            width="fit-content"
            border={{ top: { color: 'grey400', width: 's1', style: 'solid' } }}
            borderRadius={{ all: 'abs008' }}
          >
            <Table grid autoLayout overflowY="auto">
              <Table.Head>
                <Table.Row>
                  <Table.HeadCell />
                  {worksheetData[0] &&
                    worksheetData[0]
                      .slice(0, MATCH_MAX_COLUMNS)
                      .map((_, index) => (
                        <Table.HeadCell key={index}>{colName(index)}</Table.HeadCell>
                      ))}
                </Table.Row>
              </Table.Head>
              <Table.Body>
                {worksheetData.slice(0, MATCH_MAX_ROWS).map((row, rowIndex) => (
                  <Table.Row key={rowIndex}>
                    <Table.HeadCell>{rowIndex + 1}</Table.HeadCell>
                    {row.slice(0, MATCH_MAX_COLUMNS).map((value, colIndex) => (
                      <Table.Cell key={colIndex}>
                        <WorksheetCell
                          value={
                            value || (allowZeroValues && (value === 0 || value === ''))
                              ? String(value)
                              : ''
                          }
                          activeButton={activeButton}
                          isActive={some(
                            worksheetMatches,
                            (v) => !!v && v[0] === rowIndex && v[1] === colIndex
                          )}
                          isClickable={() => isClickable(rowIndex, colIndex)}
                          onClick={setCellMatch(rowIndex, colIndex, value)}
                        />
                      </Table.Cell>
                    ))}
                  </Table.Row>
                ))}
              </Table.Body>
            </Table>
          </Box>
        </Box>
      </Box>
    </Box>
  )
}

export default WorksheetTable
